// Adds various banners to desktop page header.
// Most are only activated on certain pages when certain props are included with the DynamicPageHeader component.
import * as React from 'react';

import cx from 'classnames';

import createLoadableComponent from 'js/lib/createLoadableComponent';

import type {
  GetS12nCertificateBannerProps,
  ProductDiscountPromoBannerProps,
  Program,
} from 'bundles/page/types/Program';
import type { COURSERA, DEGREE, NOT_SELECTED, PROGRAM } from 'bundles/program-home/constants/SwitcherSelectionTypes';
import { showBanner as showProductDiscountBanner } from 'bundles/promotions/utils/productDiscountPromoBannerUtils';

const LoadableGDPRBanner = createLoadableComponent(() => import('bundles/marketing-consent/components/GDPRBanner'));
const LoadableProductDiscountPromoBanner = createLoadableComponent(
  () => import('bundles/promotions/components/ProductDiscountPromoBanner')
);
const LoadableGetS12nCertificateBanner = createLoadableComponent(
  () => import('bundles/enroll/components/common/GetS12nCertificateBanner')
);

type Props = {
  showGDPRBanner?: string;
  switcherSelections?: {
    id: string;
    selectionType: typeof NOT_SELECTED | typeof PROGRAM | typeof COURSERA | typeof DEGREE;
    programId: string;
    degreeId: string;
  };
  setParentClassName: any;
  programs: Array<Program>;
  getS12nCertificateBannerProps?: GetS12nCertificateBannerProps;
  productDiscountPromoBannerProps?: ProductDiscountPromoBannerProps;
};

// @ts-expect-error
export default (Component) => {
  const WithDesktopBanner = (props: Props) => {
    const {
      showGDPRBanner,
      switcherSelections,
      setParentClassName,
      programs,
      getS12nCertificateBannerProps,
      productDiscountPromoBannerProps,
    } = props;

    // TODO(ppaskaris): Update Next header to use the same add/remove height class as DesktopHeaderControls.
    // For now, we will ensure that removeParentHeightClass is invoked before addParentHeightClass in the children.
    const addParentHeightClass = (className: string, bonusClassName?: string) =>
      setParentClassName(cx(className, bonusClassName));
    const removeParentHeightClass = () => setParentClassName('');

    return (
      <React.Fragment>
        {showGDPRBanner && switcherSelections && (
          <LoadableGDPRBanner
            addParentHeightClass={addParentHeightClass}
            removeParentHeightClass={removeParentHeightClass}
            programs={programs}
          />
        )}

        {getS12nCertificateBannerProps?.s12nSlug && (
          <LoadableGetS12nCertificateBanner
            {...getS12nCertificateBannerProps}
            addParentHeightClass={addParentHeightClass}
            removeParentHeightClass={removeParentHeightClass}
          />
        )}

        {showProductDiscountBanner({ productDiscountPromoBannerProps }) && (
          <LoadableProductDiscountPromoBanner
            {...productDiscountPromoBannerProps}
            addParentHeightClass={addParentHeightClass}
            removeParentHeightClass={removeParentHeightClass}
          />
        )}

        <Component {...props} />
      </React.Fragment>
    );
  };

  return WithDesktopBanner;
};
